import React, { useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Modal, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteServiceLdpRequest, getListServiceLdpsRequest, createServiceLdpRequest, updateServiceLdpRequest } from '../../../services/cms/serviceLdpServiceRequest';
import { listAllActiveServicesRequest } from '../../../services/cms/serviceServiceRequest';
import { newObjectKeys } from '../../../utils/utility';
import UploadButton from '../../../components/UploadButton';

const { Text } = Typography;
const { TextArea } = Input;

// Helper component: hiển thị link ảnh copyable bên dưới UploadButton
const ImageLinkDisplay = ({ value }) => {
  if (!value) return null;
  return (
    <Input
      size="small"
      readOnly
      value={value}
      style={{ marginTop: 4, fontSize: 11, color: '#1677ff' }}
      addonAfter={
        <Typography.Text
          copyable={{ text: value, tooltips: ['Copy link', 'Đã copy!'] }}
          style={{ fontSize: 11, margin: 0, lineHeight: 1 }}
        />
      }
    />
  );
};

const QuanLyServiceLdp = () => {
  const [formSearch] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const { data: dataLdps, refetch, isLoading } = useQuery({
    queryKey: ['dataServiceLdps', params],
    queryFn: () => getListServiceLdpsRequest(params),
  });

  const { data: allActiveServices } = useQuery({
    queryKey: ['allActiveServices'],
    queryFn: () => listAllActiveServicesRequest({}),
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteServiceLdpRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Xóa landing page thành công!' }); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateCreate, isPending: isCreating } = useMutation({
    mutationFn: createServiceLdpRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Tạo landing page thành công!' }); setModalOpen(false); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateUpdate, isPending: isUpdating } = useMutation({
    mutationFn: updateServiceLdpRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Cập nhật landing page thành công!' }); setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.service_id) Object.assign(temp, { service_id: values.service_id });
    if (values?.is_active >= 0) Object.assign(temp, { is_active: values.is_active });
    setParams(temp);
  };

  const openCreate = () => { formEdit.resetFields(); setEditingRecord(null); setModalOpen(true); };
  const openEdit = (record) => { setEditingRecord(record); formEdit.setFieldsValue(record); setModalOpen(true); };
  const openCopy = (record) => {
    const { id, ...rest } = record;
    setEditingRecord(null);
    formEdit.resetFields();
    formEdit.setFieldsValue(rest);
    setModalOpen(true);
  };
  const handleModalOk = () => {
    formEdit.validateFields().then(values => {
      if (editingRecord) mutateUpdate({ id: editingRecord.id, ...values });
      else mutateCreate(values);
    });
  };

  const serviceOptions = (allActiveServices || []).map(s => ({ value: s.id, label: s.name }));

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Dịch vụ', dataIndex: 'service_name', key: 'service_name', width: 120, render: (text) => text || '--' },
    { title: 'App Version', dataIndex: 'app_version', key: 'app_version', width: 100 },
    { title: 'Icon', dataIndex: 'icon', key: 'icon', width: 80,
      render: (url) => url ? <img src={url} alt="icon" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : '--',
    },
    { title: 'Thumb', dataIndex: 'thumb', key: 'thumb', width: 80,
      render: (url) => url ? <img src={url} alt="thumb" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : '--',
    },
    {
      title: 'Trạng thái', dataIndex: 'is_active', key: 'is_active', width: 100,
      render: (is_active) => <Tag color={is_active === 1 ? 'green' : 'red'}>{is_active === 1 ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Thao tác', key: 'action', width: 100,
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => openEdit(record)}><EditOutlined /></Button>
          <Button type="text" onClick={() => openCopy(record)} title="Sao chép"><CopyOutlined /></Button>
          <Popconfirm title="Xóa landing page này?" onConfirm={() => mutateDelete({ id: record?.id })} okText="Xóa" cancelText="Hủy">
            <Button type="text" danger><DeleteOutlined /></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Watch image fields to show copyable links
  const iconValue = Form.useWatch('icon', formEdit);
  const thumbValue = Form.useWatch('thumb', formEdit);
  const levelIconValue = Form.useWatch('level_icon', formEdit);
  const levelThumbValue = Form.useWatch('level_thumb', formEdit);
  const levelHighlightValue = Form.useWatch('level_highlight', formEdit);
  const iconFeatureFocusesValue = Form.useWatch('icon_feature_focuses', formEdit);
  const imageBackgroundValue = Form.useWatch('image_background', formEdit);

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Quản lý Landing Page dịch vụ</h1>
        <Button type="primary" onClick={openCreate}><PlusOutlined /> Thêm LDP</Button>
      </div>
      <Form name="search_ldps" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="service_id" label={<Text>Dịch vụ</Text>}>
              <Select showSearch allowClear placeholder="Chọn dịch vụ"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={serviceOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="is_active" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select options={[{ value: -1, label: 'Tất cả' }, { value: 1, label: 'Active' }, { value: 0, label: 'Inactive' }]} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['service_id', 'is_active'])).some((value) => value) && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <TableCustom columns={columns} dataSource={dataLdps?.data} totalItem={dataLdps?.total} params={params} setParams={setParams}
        loading={isLoading} scroll={{ x: 800 }} />

      <Modal title={editingRecord ? 'Sửa Landing Page' : 'Thêm Landing Page'} open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); }}
        onOk={handleModalOk} confirmLoading={isCreating || isUpdating} okText="Lưu" cancelText="Hủy" destroyOnClose width={1000}>
        <Form form={formEdit} layout="vertical">
          <Row gutter={12}>
            <Col span={5}>
              <Form.Item name="service_id" label="Dịch vụ" rules={[{ required: true, message: 'Chọn dịch vụ' }]}>
                <Select showSearch allowClear placeholder="Chọn dịch vụ"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={serviceOptions}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="app_version" label="App Version">
                <Input placeholder="VD: 5.0.0" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="platform" label="Nền tảng">
                <Select allowClear placeholder="Chọn nền tảng"
                  options={[
                    { value: '', label: 'Tất cả' },
                    { value: 'android', label: 'Android' },
                    { value: 'ios', label: 'iOS' },
                    { value: 'web', label: 'Web' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="is_active" label="Trạng thái" initialValue={1}>
                <Select options={[{ value: 1, label: 'Active' }, { value: 0, label: 'Inactive' }]} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="show_in_service_block" label="Hiển thị khối DV" initialValue={1}>
                <Select options={[{ value: 1, label: 'Có' }, { value: 0, label: 'Không' }]} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="deeplink" label="Deeplink">
            <Input placeholder="Nhập deeplink dịch vụ" />
          </Form.Item>

          <Form.Item name="content" label="Nội dung (mảng json encode gồm: icon, title, description)">
            <TextArea rows={3} placeholder="Nội dung landing page" />
          </Form.Item>
          <Form.Item name="metadata" label="Nội dung html">
            <TextArea rows={2} placeholder="Nội dung html" />
          </Form.Item>

          {/* === Nhóm ảnh === */}
          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 8, paddingTop: 12, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: '#1677ff' }}>Hình ảnh</span>
          </div>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="icon" label="Ảnh icon danh sách">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={iconValue} />
            </Col>
            <Col span={6}>
              <Form.Item name="thumb" label="Ảnh thumb">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={thumbValue} />
            </Col>
            <Col span={6}>
              <Form.Item name="level_icon" label="Icon cấp độ DV">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={levelIconValue} />
            </Col>
            <Col span={6}>
              <Form.Item name="level_thumb" label="Ảnh cấp độ DV">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={levelThumbValue} />
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="level_highlight" label="Ảnh highlight cấp độ DV">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={levelHighlightValue} />
            </Col>
            <Col span={6}>
              <Form.Item name="icon_feature_focuses" label="Icon tính năng nổi bật">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={iconFeatureFocusesValue} />
            </Col>
            <Col span={6}>
              <Form.Item name="image_background" label="Ảnh nền">
                <UploadButton />
              </Form.Item>
              <ImageLinkDisplay value={imageBackgroundValue} />
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default QuanLyServiceLdp;

